import { Vue, Component, Prop, Watch, Inject } from 'vue-property-decorator';
import {
  Step, StepPager, Btn, Toolbar, Field, DropdownField, Action, IStepComponent, Validations, Grid, IGridComponent, ClearAfterFunction,
  CellDoubleClickedEvent, GridReadyEvent, SelectionChangedEvent, GetRowIdFunc
} from '@samc/centricity-core';
import { IBuyer, IBuyPackage, IFastBuyPackageList, IProductReviewType, IReviewOrderPackageDetail, IReviewOrderSearch, IReviewType, ISeller, IVendorReviewType, IReviewTypeStepInput } from '@/Interfaces';
import { ReviewTypeStepInputValidator } from '@/Validators';
import { ReviewOrderDataSource } from '@/Sources';

import '@/registerVuePlugins';
import { IPackageGroup, PackageSelector } from '@/Components/PackageSelector';
import { IReviewProductGroup, ReviewProductSelector } from '@/Components/ReviewProductSelector';
import { ReviewOrderViewSideDrawer } from '@/Components/SideDrawers';
import { isNullOrEmpty } from '@/utils';


@Component({
  name: 'review-type-step',
  components: {
    Step, StepPager, Btn, Toolbar, Field, DropdownField, Action, PackageSelector, ReviewProductSelector, Grid
  },
  filters: { toString }
})
export default class ReviewTypeStep extends Vue implements IStepComponent {
  public id: string = 'process';
  @Prop() public verificationToken!: string;
  @Prop() public value?: IReviewTypeStepInput;
  @Prop({ default: null }) public initialPackage!: IFastBuyPackageList | null;
  @Prop({ default: null }) public initialBuyer!: IBuyer | null;
  @Prop({ default: null }) public initialSeller!: ISeller | null;
  @Prop({ default: null }) public initialReviewType!: IReviewType | null;
  @Prop({ required: true }) public url!: string;
  @Prop({ default: 0 }) public autoSaveId!: number | null;
  @Prop({ default: () => null }) public onlyImportProcess!: boolean | null;
  @Prop({ default: () => null }) public isException!: boolean | null;

  public isDirty: boolean = false;
  public isReadonly: boolean = false;
  public showValidations: boolean = false;
  public orderGridKey: number = 0;
  public reviewOrderId: number | null = null;
  public importProcessId: number | null = null;
  public dataSourceRefreshTimeout: number | NodeJS.Timeout | null = null;

  public package: IFastBuyPackageList | null = null;
  public buyer: IBuyer | null = null;
  public seller: ISeller | null = null;
  public reviewType: IReviewType | null = null;
  public reviewVendor: IVendorReviewType | null = null;
  public reviewProduct: IProductReviewType | null = null;
  public reviewOrder: IReviewOrderSearch | null = null;
  public packageModel: IPackageGroup = { sellerId: null, buyerId: null, packageId: null };
  public reviewModel: IReviewProductGroup = { reviewTypeId: null, vendorId: null, productId: null };
  public reviewOrderDataSource: ReviewOrderDataSource | null = null;
  public isInitialized: boolean = false;
  public getRowId: GetRowIdFunc<IReviewOrderSearch> = x => x.data.id.toString();

  @Inject('clearAfter') public clearAfter!: ClearAfterFunction;

  /**
   * The wrapper for the component instance
   */
  public get component(): IStepComponent | null {
    return {
      isValid: this.isValid,
      isDirty: this.isDirty,
      isReadonly: this.isReadonly,
      clear: this.clear,
      initializeEvents: this.initializeEvents,
      clearEvents: this.clearEvents,
      initialize: this.initialize,
      enter: this.enter,
      skip: this.skip,
      leave: this.leave
    };
  };

  public get model(): IReviewTypeStepInput { return { ...this.packageModel, ...this.reviewModel, reviewOrderId: this.reviewOrderId, importProcessId: this.importProcessId }; }
  public get validations(): Validations<IReviewTypeStepInput> { return new ReviewTypeStepInputValidator().isValid(this.model); }
  public get isValid(): boolean { return Object.keys(this.validations).length === 0; }
  public get description(): string {
    return this.reviewOrder?.clientRefId
      ?? this.package?.packageName
      ?? this.reviewProduct?.productName
      ?? this.reviewVendor?.vendorName
      ?? this.reviewType?.name
      ?? this.seller?.name
      ?? this.buyer?.name
      ?? '';
  }

  public async initialize(): Promise<boolean> {
    this.reviewOrderDataSource = new ReviewOrderDataSource(this.url, this.reviewOrderId, this.reviewModel.reviewTypeId, this.reviewModel.vendorId, this.reviewModel.productId, this.packageModel.packageId, this.packageModel.buyerId, this.packageModel.sellerId, data => {
      const grid = ((this.$refs['reviewOrderGrid'] as any) ?? null) as IGridComponent | null;
      if (grid !== null && grid.gridApi && this.reviewOrderId !== null) {
        const node = grid.gridApi.getRowNode(this.reviewOrderId.toString());
        if (node !== undefined) {
          node.setSelected(true);
        }
      }
    });
    return true;
  }

  public async clear(): Promise<void> {
    this.package = null;
    this.buyer = null;
    this.seller = null;
    this.reviewType = null;
    this.reviewVendor = null;
    this.reviewProduct = null;
    this.reviewOrder = null;

    this.isDirty = false;
    this.isReadonly = false;
    this.showValidations = false;
    this.orderGridKey = 0;
    this.reviewOrderId = null;
    this.importProcessId = null;
    this.dataSourceRefreshTimeout = null;

    this.packageModel = { sellerId: null, buyerId: null, packageId: null };
    this.reviewModel = { reviewTypeId: null, vendorId: null, productId: null };

    this.onInitialPackageChange(this.initialPackage);
    this.onInitialBuyerChange(this.initialBuyer);
    this.onInitialSellerChange(this.initialSeller);
    this.onInitialReviewTypeChange(this.initialReviewType);
  }

  public async enter(): Promise<void> {
    // Nothing needs to be done on enter
  }

  public async skip(): Promise<void> {
    // Nothing needs to be done on skip
  }

  /**
   * Intialize events for the event bus
   **/
  public async initializeEvents(): Promise<void> {
    // Do Nothing
  }

  /**
   * Remove events from the event bus
   **/
  public async clearEvents(): Promise<void> {
    // Do Nothing
  }

  public async leave(dir: 1 | -1): Promise<boolean> {
    if (dir === 1) {
      this.showValidations = true;
      if (Object.keys(this.validations).length > 0) {
        this.$alert.error('Please fix validation issues');
        return false;
      }
    }
    return true;
  }

  public onReviewOrderChanged(e: SelectionChangedEvent) {
    const items = e.api.getSelectedRows() as IReviewOrderSearch[];
    this.reviewOrder = items.length > 0 ? items[0] : null;
    const reviewOrderId = this.reviewOrder?.id ?? null;

    if (this.reviewOrderId !== reviewOrderId) {
      this.reviewOrderId = reviewOrderId;
      this.reviewModel = {
        reviewTypeId: this.reviewOrder?.reviewTypeId ?? null,
        vendorId: this.reviewOrder?.organizationId ?? null,
        productId: this.reviewOrder?.productId ?? null
      };

      if (this.reviewOrder?.packageIds.length === 1) {
        this.packageModel.packageId = this.reviewOrder.packageIds[0];
      }
      this.$emit('input', this.model);
    }
  }

  public onGridReady(e: GridReadyEvent) {
    setTimeout(() => {
      if (this.reviewOrderId !== null) {
        const node = e.api.getRowNode(this.reviewOrderId.toString());
        if (node !== undefined) {
          node.setSelected(true);
        }
      }
    });
  }

  public onReviewOrderRowDoubleClick(e: CellDoubleClickedEvent) {
    const order = e.data as IReviewOrderSearch;
    this.$sideDrawer<true | number[]>({
      width: '75%',
      component: ReviewOrderViewSideDrawer,
      props: {
        hideEdit: true,
        orderTitle: order.clientRefId,
        reviewOrderId: order.id
      }
    });
  }

  private updateDataSource() {
    const grid = ((this.$refs['reviewOrderGrid'] as any) ?? null) as IGridComponent | null;
    if (grid !== null) {
      if (this.dataSourceRefreshTimeout !== null) {
        clearTimeout(this.dataSourceRefreshTimeout);
        this.dataSourceRefreshTimeout = null;
      }

      this.dataSourceRefreshTimeout = setTimeout(() => {
        grid.refreshDatasource();
        this.dataSourceRefreshTimeout = null;
      }, 100);
    }
  }

  @Watch('packageModel', { deep: true })
  @Watch('reviewModel', { deep: true })
  @Watch('importProcessId')
  @Watch('reviewOrderId')
  @Watch('buyerId')
  @Watch('sellerId')
  public onPackageModel() {
    if (this.value !== undefined) {
      this.$emit('input', this.model);
    }
  }

  @Watch('reviewOrderId')
  public onReviewOrderIdChange(newReviewOrderId: number | null) {
    if (this.reviewOrderId === null || this.reviewOrderId !== newReviewOrderId) {
      this.clearAfter(this.id);
    }

    if (this.reviewOrderDataSource !== null) {
      this.reviewOrderDataSource.id = newReviewOrderId;
      this.updateDataSource();
    }
  }

  @Watch('reviewModel', { deep: true, immediate: true })
  public onProcessChange(newModel: IReviewProductGroup) {
    if (this.reviewModel === null || this.reviewModel !== newModel) {
      this.clearAfter(this.id);
    }

    if (this.reviewOrderDataSource !== null) {
      this.reviewOrderDataSource.reviewTypeId = isNullOrEmpty(newModel.reviewTypeId) ? null : newModel.reviewTypeId;
      this.reviewOrderDataSource.organizationId = isNullOrEmpty(newModel.vendorId) ? null : newModel.vendorId;
      this.reviewOrderDataSource.productId = isNullOrEmpty(newModel.productId) ? null : newModel.productId;
      this.updateDataSource();
    }
  }

  @Watch('value', { immediate: true, deep: true })
  public onValueChange(newValue?: IReviewTypeStepInput) {
    if (newValue === undefined) return;

    this.packageModel.buyerId = newValue.buyerId;
    this.packageModel.sellerId = newValue.sellerId;
    this.packageModel.packageId = newValue.packageId;
    this.reviewModel.reviewTypeId = newValue.reviewTypeId;
    this.reviewModel.vendorId = newValue.vendorId;
    this.reviewModel.productId = newValue.productId;
    this.reviewOrderId = newValue.reviewOrderId;

    if (this.reviewOrderDataSource !== null) {
      this.reviewOrderDataSource.reviewTypeId = this.reviewModel.reviewTypeId;
      this.reviewOrderDataSource.organizationId = this.reviewModel.vendorId;
      this.reviewOrderDataSource.productId = this.reviewModel.productId;
      this.updateDataSource();
    }
  }

  @Watch('initialPackage', { immediate: true })
  public onInitialPackageChange(newInitialPackage: IFastBuyPackageList | null) {
    this.packageModel.packageId = newInitialPackage?.id ?? null;
    this.package = newInitialPackage;
  }

  @Watch('initialBuyer', { immediate: true })
  public onInitialBuyerChange(newInitialBuyer: IBuyer | null) {
    this.packageModel.buyerId = newInitialBuyer?.id ?? null;
    this.buyer = newInitialBuyer;
  }

  @Watch('initialSeller', { immediate: true })
  public onInitialSellerChange(newInitialSeller: ISeller | null) {
    this.packageModel.sellerId = newInitialSeller?.id ?? null;
    this.seller = newInitialSeller;
  }

  @Watch('initialReviewType', { immediate: true })
  public onInitialReviewTypeChange(newInitialReviewType: IReviewType | null) {
    if (newInitialReviewType !== null) {
      this.reviewModel.reviewTypeId = this.initialReviewType?.id ?? null;
      if (this.reviewModel.reviewTypeId !== null || this.reviewModel.reviewTypeId !== newInitialReviewType.id) {
        this.clearAfter(this.id);
      }
      this.reviewType = newInitialReviewType;
      this.importProcessId = newInitialReviewType.importProcessId;
    }
  }

  @Watch('packageModel.packageId', { immediate: true })
  public onPackageIdChange(newPackageId: string | null) {
    if ((this.initialPackage?.packageId.toString() ?? null) !== newPackageId) {
      this.clearAfter(this.id);
    }

    if (this.reviewOrderDataSource !== null) {
      this.reviewOrderDataSource.packageId = newPackageId;
      this.updateDataSource();
    }
  }

  @Watch('packageModel.buyerId', { immediate: true })
  public onBuyerIdChange(newBuyerId: string | null) {
    if (isNullOrEmpty(this.initialBuyer?.id ?? null) || this.initialBuyer?.id !== newBuyerId) {
      this.clearAfter(this.id);
    }

    if (this.reviewOrderDataSource !== null) {
      this.reviewOrderDataSource.buyerId = newBuyerId;
      this.updateDataSource();
    }
  }

  @Watch('packageModel.sellerId', { immediate: true })
  public onSellerIdChange(newSellerId: string | null) {
    if (isNullOrEmpty(this.initialSeller?.id ?? null) || this.initialSeller?.id !== newSellerId) {
      this.clearAfter(this.id);
    }

    if (this.reviewOrderDataSource !== null) {
      this.reviewOrderDataSource.sellerId = newSellerId;
      this.updateDataSource();
    }
  }

  @Watch('initialReviewTypeId', { immediate: true })
  public onInitialReviewTypeIdChange(newinitialReviewTypeId: string | null) {
    if (this.initialReviewType === null || this.initialReviewType.id !== newinitialReviewTypeId) {
      this.clearAfter(this.id);
    }
  }

  @Watch('reviewType', { deep: true })
  public onReviewTypeIdChange(newReviewType: IReviewType | null, oldReviewType: IReviewType | null) {
    if (newReviewType !== null && newReviewType?.id !== oldReviewType?.id) {
      this.importProcessId = newReviewType.importProcessId;
      this.clearAfter(this.id);
    }
  }

  @Watch('isValid')
  public onIsValidChange(newIsValid: boolean, oldIsValid: boolean) {
    if (!oldIsValid && newIsValid) {
      this.$alert.close();
    }
  }
}